import { useState, memo } from "react";
import { ChevronDown, MessageCircle, Calendar, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

// FAQ data with personalization placeholder
const getFaqData = (listenerName) => [
  {
    question: "¿Esto es terapia?",
    answer: `No. Es un espacio donde puedes hablar libremente con alguien que te escucha sin juzgar. A veces no necesitas terapia formal, solo necesitas desahogarte y sentirte entendido.`,
    highlight: "✨ No tienes que estar 'mal' para hablar con alguien."
  },
  {
    question: "¿En qué me puede ayudar una sesión?",
    answer: `En una conversación con ${listenerName}, puedes soltar lo que traes dentro, ordenar tus pensamientos y sentirte escuchado. Muchas personas terminan la sesión con más claridad y menos peso encima.`
  },
  {
    question: "¿Tengo que tomar varias sesiones?",
    answer: "No. Puedes tener solo una conversación y ver cómo te sientes. Sin suscripciones ni compromisos."
  },
  {
    question: "¿Es en español?",
    answer: `Sí. Hablas directamente con ${listenerName}, en tu idioma.`
  },
  {
    question: "¿Cómo funciona?",
    answer: "Eliges tu horario, agendas y te conectas por videollamada desde tu celular o computadora. Es simple y privado."
  }
];

// Single FAQ Item component
const FaqItem = memo(({ question, answer, highlight, isOpen, onToggle, index }) => {
  return (
    <div 
      className="border-b border-stone-100 last:border-0"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={onToggle}
        className="w-full py-4 px-1 flex items-center justify-between text-left focus:outline-none group"
        aria-expanded={isOpen}
      >
        <span className="text-base font-medium text-stone-800 pr-4 group-hover:text-primary transition-colors">
          {question}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-stone-400 flex-shrink-0 transition-transform duration-200 ease-out ${
            isOpen ? 'rotate-180 text-primary' : ''
          }`}
        />
      </button>
      
      <div 
        className={`overflow-hidden transition-all duration-200 ease-out ${
          isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="text-stone-600 text-sm leading-relaxed px-1">
          {answer}
        </p>
        {highlight && (
          <p className="mt-3 text-sm text-primary/90 font-medium px-1">
            {highlight}
          </p>
        )}
      </div>
    </div>
  );
});

FaqItem.displayName = 'FaqItem';

// Main FAQ Module component
const ListenerFaqModule = memo(({ listenerName, onBookClick, onInstantClick, showInstant = false }) => {
  const [openIndex, setOpenIndex] = useState(0); // First one open by default
  const faqData = getFaqData(listenerName || "tu listener");

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div 
      className="bg-gradient-to-b from-stone-50 to-white rounded-2xl p-5 shadow-sm border border-stone-100"
      data-testid="faq-module"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold text-stone-800">Preguntas frecuentes</h3>
      </div>

      {/* FAQ Accordion */}
      <div className="space-y-0">
        {faqData.map((faq, index) => (
          <FaqItem
            key={index}
            index={index}
            question={faq.question}
            answer={faq.answer}
            highlight={faq.highlight}
            isOpen={openIndex === index}
            onToggle={() => toggleFaq(index)}
          />
        ))}
      </div>

      {/* Conversion Booster Section */}
      <div className="mt-6 pt-5 border-t border-stone-100">
        {/* Reinforcement Text */}
        <div className="bg-blue-50/50 rounded-xl p-4 mb-4">
          <p className="text-sm text-stone-700 leading-relaxed flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span>
              <span className="font-medium">Muchas personas llegan sintiéndose abrumadas…</span>
              {" "}y se van sintiéndose más ligeras.
            </span>
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onBookClick}
            className="flex-1 py-5 text-base font-semibold bg-primary hover:bg-primary/90 rounded-xl"
            data-testid="faq-book-btn"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Agendar sesión
          </Button>
          
          {showInstant && (
            <Button
              onClick={onInstantClick}
              variant="outline"
              className="flex-1 py-5 text-base font-semibold border-green-500 text-green-600 hover:bg-green-50 rounded-xl"
              data-testid="faq-instant-btn"
            >
              Hablar ahora
            </Button>
          )}
        </div>

        {/* Subtle Disclaimer */}
        <p className="text-xs text-stone-400 text-center mt-4">
          Este servicio no sustituye terapia profesional.
        </p>
      </div>
    </div>
  );
});

ListenerFaqModule.displayName = 'ListenerFaqModule';

export default ListenerFaqModule;
